import { useQuery } from '@tanstack/react-query'
import { billingApi } from '@/api/billing'
import { Loading } from '@/components/ui'
import { formatNIS } from '@/utils/currency'
import type { LineItem } from '@/types/billing'

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  active: { bg: '#22c55e20', text: '#4ade80' },
  trial: { bg: '#f59e0b20', text: '#fbbf24' },
  suspended: { bg: '#ef444420', text: '#f87171' },
  cancelled: { bg: '#6b728020', text: '#9ca3af' },
}

interface Props {
  orgId: string
  planLine: LineItem | null
}

export default function PlanBlock({ orgId, planLine }: Props) {
  const { data: central, isLoading } = useQuery({
    queryKey: ['admin-billing-central', orgId],
    queryFn: () => billingApi.getCentralSubscriptionStatus(orgId),
    staleTime: 60_000,
  })

  if (isLoading && !planLine) {
    return (
      <div className="card p-6 border border-navy-border bg-[#1a1d27]">
        <Loading text="Loading plan…" />
      </div>
    )
  }

  if (!planLine || planLine.type !== 'plan') {
    return (
      <div className="card p-6 border border-navy-border bg-[#1a1d27]">
        <h3 className="text-sm font-semibold text-ink-primary mb-2">Plan</h3>
        <p className="text-sm text-ink-secondary">No plan line item in the current snapshot.</p>
      </div>
    )
  }

  const st = (central?.status ?? '').toLowerCase()
  const colors = STATUS_COLORS[st] ?? STATUS_COLORS.cancelled

  return (
    <div className="card p-6 border border-navy-border bg-[#1a1d27]">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="text-sm font-semibold text-ink-primary">Plan</h3>
        {central?.status && (
          <span
            className="text-xs font-medium px-2 py-0.5 rounded"
            style={{ backgroundColor: colors.bg, color: colors.text }}
          >
            {central.status}
          </span>
        )}
      </div>
      <div className="flex flex-wrap items-end justify-between gap-2 border-t border-navy-border pt-4">
        <div>
          <p className="text-lg font-medium text-ink-primary">{planLine.label}</p>
          <p className="text-xs text-ink-muted mt-1">
            {planLine.system}
            {planLine.plan_code ? ` · ${planLine.plan_code}` : ''}
          </p>
        </div>
        <div className="text-right">
          <p className="text-xs text-ink-secondary">Plan fee (monthly)</p>
          <p className="text-xl font-semibold text-indigo-300 num">{formatNIS(planLine.amount_agorot)}</p>
        </div>
      </div>
    </div>
  )
}
